import { useEffect, useMemo, useRef, useState } from 'react';
import { useCommissionData } from '../contexts/CommissionDataContext';
import { periods } from '../data/commissions';
import { officers } from '../data/officers';
import { BpsAdjustment, ResolvedRequirement } from '../types/commission';
import { formatBps } from '../utils/commission';
import { adjustmentKey as buildAdjustmentKey, computeOfficerPeriod } from '../utils/officerPeriod';
import { buildQualifyingLoans } from '../utils/production';

const LOAD_DELAY = 450;
const RECALCULATE_DELAY = 1200;

interface DashboardOptions {
  officerId: string;
  simulateLoadError: boolean;
}

/** State for one user's commission statement. */
export function useCommissionDashboard({ officerId, simulateLoadError }: DashboardOptions) {
  const { periodId, setPeriodId, adjustments, applyAdjustment, overrides, saveOverride } = useCommissionData();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [recalculateSaving, setRecalculateSaving] = useState(false);
  const [recalculateError, setRecalculateError] = useState<string | null>(null);
  const [recalculateSuccess, setRecalculateSuccess] = useState<string | null>(null);
  const [qualifyingLabel, setQualifyingLabel] = useState<string | null>(null);
  const recalcTimer = useRef<number>();

  const officer = officers.find((item) => item.id === officerId) ?? officers[0];
  const period = periods.find((item) => item.id === periodId) ?? periods[0];
  const result = useMemo(
    () => computeOfficerPeriod(officer, period, adjustments, overrides),
    [officer, period, adjustments, overrides],
  );
  const { role, loans, periodRate } = result;
  const adjustmentKey = buildAdjustmentKey(officer.id, period.id);

  const requirements: ResolvedRequirement[] = useMemo(
    () =>
      (role.promotion?.requirements ?? []).map((requirement) => {
        if (!requirement.qualifyingMonths) return { ...requirement, isExpandable: false };
        const { loans: qualifying } = buildQualifyingLoans(officer, role, period.id, requirement.qualifyingMonths);
        return { ...requirement, value: qualifying.length, isExpandable: true };
      }),
    [officer, role, period.id],
  );

  const qualifyingRequirement = requirements.find((item) => item.isExpandable && item.label === qualifyingLabel) ?? null;
  const qualifying = useMemo(
    () =>
      qualifyingRequirement
        ? buildQualifyingLoans(officer, role, period.id, qualifyingRequirement.qualifyingMonths ?? 1)
        : null,
    [qualifyingRequirement, officer, role, period.id],
  );

  // Simulated server load whenever the viewed user or pay period changes.
  useEffect(() => {
    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      setLoading(false);
      if (simulateLoadError && retryCount === 0) {
        setError('The commission service didn’t respond. Check your connection and try again.');
      }
    }, LOAD_DELAY);
    return () => window.clearTimeout(timer);
  }, [officer.id, period.id, retryCount, simulateLoadError]);

  useEffect(() => {
    window.clearTimeout(recalcTimer.current);
    setRecalculateSaving(false);
    setRecalculateError(null);
    setRecalculateSuccess(null);
    setQualifyingLabel(null);
  }, [officer.id, period.id]);

  useEffect(() => () => window.clearTimeout(recalcTimer.current), []);

  function handleRecalculatePayPeriod() {
    setRecalculateError(null);
    setRecalculateSuccess(null);
    setRecalculateSaving(true);
    recalcTimer.current = window.setTimeout(() => {
      setRecalculateSaving(false);
      if (loans.length === 0) {
        setRecalculateError(`There are no payment line items to recalculate for ${period.label}.`);
        return;
      }
      setRecalculateSuccess(
        `${period.label} recalculated for ${officer.name}. ${loans.length} ${
          loans.length === 1 ? role.unitNoun : role.unitNounPlural
        } re-rated${periodRate !== null ? ` at ${formatBps(periodRate)}` : ''}.`,
      );
    }, RECALCULATE_DELAY);
  }

  return {
    ...result,
    periods,
    setSelectedPeriodId: setPeriodId,
    isBps: role.model === 'bps',
    isProductionRole: role.group === 'Production',
    isLockedPeriod: period.status === 'locked',
    hasData: loans.length > 0,
    loading,
    error,
    handleRetry: () => setRetryCount((count) => count + 1),
    recalculateSaving,
    recalculateError,
    recalculateSuccess,
    handleRecalculatePayPeriod,
    adjustmentKey,
    applyPeriodAdjustment: (value: BpsAdjustment | null) => applyAdjustment(adjustmentKey, value),
    saveOverride,
    requirements,
    qualifyingRequirement,
    qualifying,
    openQualifyingLoans: setQualifyingLabel,
    closeQualifyingLoans: () => setQualifyingLabel(null),
  };
}
